import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Form, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';
import { useIsQueryEnabled } from '../hooks/useIsQueryEnabled';
import { useWarningsSummaryDataLoader } from '../loaders/StudentWarningsSummaryDataLoader';
import { useWarningsSummaryFormLoaderData } from '../loaders/StudentWarningsSummaryFormLoader';
import { StudentWarningsGroupType, StudentWarningsRiskLevel } from '../types';

type StudentWarningsSummarySecondaryFiltersParams = {
  year?: SelectOptionType | null;
  period?: SelectOptionType | null;
  riskLevel?: SelectOptionType | null;
  studyPlan?: SelectOptionType | null;
  campus?: SelectOptionType | null;
  startAt?: string | null;
  endAt?: string | null;
  name?: string;
};

const defaultValues: StudentWarningsSummarySecondaryFiltersParams = {
  year: null,
  period: null,
  riskLevel: null,
  studyPlan: null,
  campus: null,
  startAt: null,
  endAt: null,
  name: '',
};

const StudentWarningsSummarySecondaryFilters = () => {
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_SUMMARY);
  const data = useWarningsSummaryFormLoaderData();
  const { loading, query, updateQuery } = useWarningsSummaryDataLoader();
  const {
    handleSubmit,
    reset,
    control,
  } = useForm<StudentWarningsSummarySecondaryFiltersParams>({
    defaultValues,
  });

  const isQueryEnabled = useIsQueryEnabled(query);
  const [mounted, setMounted] = useState(false);

  const includeAcademicFilters = useMemo(
    () =>
      query?.warningGroupType === 'summary' ||
      query?.warningGroupType === StudentWarningsGroupType.Academic,
    [query?.warningGroupType],
  );

  const riskLevelsOptions: SelectOptionType[] = useMemo(
    () => [
      {
        label: t('riskLevels.low'),
        value: StudentWarningsRiskLevel.Low,
      },
      {
        label: t('riskLevels.medium'),
        value: StudentWarningsRiskLevel.Medium,
      },
      {
        label: t('riskLevels.high'),
        value: StudentWarningsRiskLevel.High,
      },
    ],
    [t],
  );

  const handleSearch = useCallback(
    (filters: StudentWarningsSummarySecondaryFiltersParams) => {
      updateQuery({
        ...query,
        page: 1,
        riskLevel: filters.riskLevel?.value as string,
        studyPlanId: filters.studyPlan?.value as string,
        campusId: filters.campus?.value as string,
        name: filters?.name?.trim() || undefined,
        startAt: filters?.startAt || undefined,
        endAt: filters?.endAt || undefined,
      });
    },
    [updateQuery, query],
  );

  const handleClear = useCallback(() => {
    reset(defaultValues);
    updateQuery({
      ...query,
      page: 1,
      riskLevel: undefined,
      studyPlanId: undefined,
      campusId: undefined,
      name: undefined,
      startAt: undefined,
      endAt: undefined,
    });
  }, [reset, query, updateQuery]);

  useEffect(() => {
    if (!mounted && query && data) {
      reset({
        ...defaultValues,
        name: query.name?.trim() || '',
        studyPlan: query.studyPlanId
          ? data.studyPlans.find((p) => p.value === query.studyPlanId) || null
          : null,
        campus: query.campusId
          ? data.campuses.find((p) => p.value === query.campusId) || null
          : null,
        startAt: query?.startAt || null,
        endAt: query?.endAt || null,
      });
      setMounted(true);
    }
  }, [query, data, reset, mounted]);

  return (
    <Container className="mt-5" fluid>
      <Form onSubmit={handleSubmit(handleSearch)}>
        <Row className="pb-4 gutters-filters">
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="riskLevel"
              options={riskLevelsOptions}
              label={t('filters.riskLevel.label')}
              placeholder={t('filters.riskLevel.placeholder')}
              control={control}
              isClearable={true}
              disabled={
                !isQueryEnabled || loading || riskLevelsOptions.length < 1
              }
            />
          </Col>
          <Col xs={12} md={3}>
            <TextOutlinedInput
              name="name"
              label={t('filters.name.label')}
              placeholder={t('filters.name.placeholder')}
              control={control}
              disabled={!isQueryEnabled || loading}
            />
          </Col>
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="studyPlan"
              options={data.studyPlans}
              label={t('filters.studyPlan.label')}
              placeholder={t('filters.studyPlan.placeholder')}
              control={control}
              isClearable={true}
              disabled={
                !isQueryEnabled || loading || data.studyPlans.length < 1
              }
            />
          </Col>
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="campus"
              options={data.campuses}
              label={t('filters.campus.label')}
              placeholder={t('filters.campus.placeholder')}
              control={control}
              isClearable={true}
              disabled={!isQueryEnabled || loading || data.campuses.length < 1}
            />
          </Col>
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="promo"
              options={[]}
              label={t('filters.promo.label')}
              placeholder={t('filters.promo.placeholder')}
              isClearable={true}
              disabled={true}
            />
          </Col>
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="course"
              options={[]}
              label={t('filters.course.label')}
              placeholder={t('filters.course.placeholder')}
              isClearable={true}
              disabled={true}
            />
          </Col>
          {!includeAcademicFilters && (
            <Col xs={12} md={6} className="mt-4">
              <div className="d-flex justify-content-end">
                <Button
                  text={t('common:actions.search')}
                  size="md"
                  type="submit"
                  disabled={!isQueryEnabled || loading}
                />
                <Button
                  text={t('common:actions.clean')}
                  className="ml-2"
                  size="md"
                  outlined
                  type="button"
                  disabled={!isQueryEnabled || loading}
                  onClick={handleClear}
                />
              </div>
            </Col>
          )}
        </Row>
        {!!includeAcademicFilters && (
          <Row>
            <Col xs={12} md={3}>
              <TextOutlinedInput
                type={'date' as any}
                name="startAt"
                label={t('filters.startAt.label')}
                control={control}
                disabled={!isQueryEnabled || loading || data.periods.length < 1}
                tooltip={t('filters.startAt.tooltip')}
              />
            </Col>

            <Col xs={12} md={3}>
              <TextOutlinedInput
                type={'date' as any}
                name="endAt"
                label={t('filters.endAt.label')}
                control={control}
                disabled={!isQueryEnabled || loading || data.periods.length < 1}
                tooltip={t('filters.endAt.tooltip')}
              />
            </Col>
            <Col xs={12} md={includeAcademicFilters ? 6 : 12} className="mt-4">
              <div className="d-flex justify-content-end">
                <Button
                  text={t('common:actions.search')}
                  size="md"
                  type="submit"
                  disabled={!isQueryEnabled || loading}
                />
                <Button
                  text={t('common:actions.clean')}
                  className="ml-2"
                  size="md"
                  outlined
                  type="button"
                  disabled={!isQueryEnabled || loading}
                  onClick={handleClear}
                />
              </div>
            </Col>
          </Row>
        )}
      </Form>
    </Container>
  );
};

export default StudentWarningsSummarySecondaryFilters;
